/**
 * The viewing session identity.
 *
 * Anonymous viewers still need a stable id: the API dedups view counts per
 * (video, session, UTC day) and binds every playback token to a session
 * (`sid` in packages/shared/src/tokens.ts). The server cannot derive one for a
 * logged-out viewer with no cookie, so the browser mints it once and keeps it.
 *
 * It is deliberately NOT the user id. A session survives logout and is shared
 * by every tab in the browser profile, which is exactly the granularity view
 * dedup wants: reloading the watch page must not count a second view.
 */

const STORAGE_KEY = 'streamr.sid';

/** Matches `sessionIdSchema` in @streamr/shared: 8..64 chars. */
const isWellFormed = (value: string): boolean => value.length >= 8 && value.length <= 64;

const mint = (): string => {
  const globalCrypto = globalThis.crypto as Crypto | undefined;
  if (globalCrypto?.randomUUID) return globalCrypto.randomUUID();
  if (globalCrypto?.getRandomValues) {
    const bytes = globalCrypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  // Last resort: still 8..64 chars, still unique enough for view dedup.
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};

let cached: string | null = null;

/**
 * Storage can throw outright (Safari private mode, site data blocked), not just
 * return null, so every access is guarded. A viewer whose storage is unavailable
 * gets a per-tab session rather than an error: views may double count for them,
 * which is a far better failure than a watch page that will not load.
 */
export const getSessionId = (): string => {
  if (cached !== null) return cached;

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored !== null && isWellFormed(stored)) {
      cached = stored;
      return stored;
    }
  } catch {
    // fall through to minting an in-memory session
  }

  const minted = mint();
  cached = minted;
  try {
    window.localStorage.setItem(STORAGE_KEY, minted);
  } catch {
    // in-memory only for this tab
  }
  return minted;
};

/** Test seam. */
export const resetSessionIdCache = (): void => {
  cached = null;
};
